import { cssToken, rem } from "@certn/cascadia";
import { CascadiaColor, CascadiaLayout, layouts } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { useContext, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

import { LayoutContext } from "./LayoutContext";
import { mq } from "../../components/utils";

export interface LayoutProps {
  layout?: CascadiaLayout;
  header?: React.ReactNode;
  navigation?: React.ReactNode;
  /** @deprecated */
  sidebar?: React.ReactNode;
  footer?: React.ReactNode;
  contentHeader?: React.ReactNode;
  rightSideBar?: React.ReactNode;
  backgroundColor?: CascadiaColor | false;
  children: React.ReactNode;
  testID?: string;
  menuPosition?: "left" | "right";
  height?: string;
}

export function Layout(props: LayoutProps) {
  const {
    layout = "narrow",
    header,
    navigation,
    sidebar,
    footer,
    contentHeader,
    rightSideBar,
    children,
    testID = "layout",
    backgroundColor = "surface-base",
    menuPosition = "right",
    height = "100vh",
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState<string>();

  const layoutValues = layouts[layout];

  const navigationBar = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(navigationBar, () => {
    setMenuOpen(false);
  });

  return (
    <LayoutContext.Provider
      value={{
        hasNavigation: !!navigation,
        menuPosition,
        layout,
        toggleMenu: (open?: boolean) => {
          if (open !== undefined) {
            setMenuOpen(open);
          } else {
            setMenuOpen((curr) => !curr);
          }
        },
        menuOpen,
        search,
        setSearch,
      }}
    >
      <div
        style={{ backgroundColor: cssToken(backgroundColor) }}
        className={css(
          styles.container,
          css({
            minHeight: height,
          }),
          mq({
            height: [undefined, height],
          })
        )}
        data-testid={testID}
      >
        {header && <header className={styles.header}>{header}</header>}
        <div className={styles.body}>
          {sidebar && <nav className={styles.nav}>{sidebar}</nav>}
          {navigation && (
            <>
              <div
                ref={navigationBar}
                className={css(
                  styles.navigation,
                  mq({
                    left: menuPosition === "left" && [
                      menuOpen ? 0 : -384,
                      menuOpen ? 0 : -384,
                      "auto",
                    ],
                    right: menuPosition === "right" && [
                      menuOpen ? 0 : -384,
                      menuOpen ? 0 : -384,
                      "auto",
                    ],
                  })
                )}
              >
                {navigation}
              </div>
              <div
                style={{ opacity: !menuOpen ? 0 : undefined }}
                className={styles.scrim}
              />
            </>
          )}
          <div className={css(styles.contentArea)}>
            {contentHeader && (
              <div className={styles.contentHeader}>{contentHeader}</div>
            )}
            <div
              className={css(
                styles.children,
                mq({
                  rowGap: layoutValues.gapHorizontal.map(rem),
                  columnGap: layoutValues.gapVertical.map(rem),
                  paddingLeft: layoutValues.marginHorizontal.map(rem),
                  paddingRight: layoutValues.marginHorizontal.map(rem),
                  paddingTop: layoutValues.marginVertical.map(rem),
                  paddingBottom: layoutValues.marginVertical.map(rem),
                })
              )}
            >
              {children}
            </div>
          </div>
          {rightSideBar && (
            <div className={styles.rightSideBar}>{rightSideBar}</div>
          )}
        </div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </LayoutContext.Provider>
  );
}

export function useLayout(override?: CascadiaLayout) {
  const context = useContext(LayoutContext);
  const layout = override || context.layout;
  return {
    ...context,
    layout,
    layoutValues: layouts[layout],
  };
}

export function Space() {
  return <div className={css({ flex: 1 })} />;
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: 1,
  }),
  header: css({
    backgroundColor: "var(--background-base)",
    borderBottom: `1px solid var(--border-base)`,
  }),
  body: css({
    flex: 1,
    display: "flex",
    flexDirection: "row",
    position: "relative",
    height: "100%",
    overflow: "hidden",
  }),
  children: css({
    flexGrow: 1,
    overflow: "auto",
  }),
  nav: css(
    { backgroundColor: "var(--surface-base)", height: "100vh", zIndex: 1 },
    mq({
      position: ["absolute", "absolute", "relative"],
      minWidth: ["100%", "320px"],
    })
  ),
  navigation: css(
    {
      display: "flex",
      flexDirection: "column",
      maxWidth: 384,
      position: "fixed",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      transition: "left 200ms, right 200ms",
      backgroundColor: "var(--background-base)",
      borderColor: "var(--border-base)",
    },
    mq({
      top: [0, 0, "auto"],
      bottom: [0, 0, "auto"],
      width: ["100%", "auto", "auto"],
      borderRightWidth: [0, 0, 1],
      borderLeftWidth: [1, 1, 0],
      position: ["fixed", "fixed", "inherit"],
      zIndex: [100, 100, "auto"],
    })
  ),
  scrim: css({
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    transition: "opacity 200ms",
    background: "var(--surface-modal)",
    zIndex: 99,
    pointerEvents: "none",
  }),
  footer: css(
    {
      alignItems: "center",
      gap: "var(--space-4)",
      display: "flex",
      padding: "var(--space-4)",
      justifyContent: "space-between",
      paddingLeft: "var(--space-6)",
      paddingRight: "var(--space-6)",
      borderTop: `1px solid var(--border-base)`,
      backgroundColor: "var(--background-base)",
    },
    mq({
      flexDirection: ["column", "row"],
    })
  ),
  contentArea: css({
    width: "100%",
    overflowY: "auto",
  }),
  contentHeader: css({
    backgroundColor: "var(--background-base)",
    borderBottom: "solid 1px var(--border-base)",
  }),
  rightSideBar: css({
    height: "100%",
    borderLeft: `1px solid var(--border-base)`,
    backgroundColor: "var(--background-base)",
  }),
};
